import { Button, Col, Form, Row, Steps, theme, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Server, Cloud, RefreshCwIcon, Copy } from 'lucide-react'; // Lucide icons

import TextField from '../../components/formComponents/TextField';
import CustomSelectOptions from '../../components/formComponents/CustomSelectOptions';
import LoadingButton from '../../components/LoadingButton';
import { client2 } from '../../operationalRequest';
import ImportDevices from './ImportDevices';
import _ from 'lodash';
import toastr from "toastr";


type IntegrateProps = {
    mutate?: any;
};

const Integrate: React.FC<IntegrateProps> = ({ mutate }) => {
    const { token } = theme.useToken();
    const [current, setCurrent] = useState(0);
    const [initialValue, setInitialValue] = useState({} as any);
    const [loading, setLoading] = useState(false);
    const [integrationId, setIntegrationId] = useState(null);

    const next = () => setCurrent((prev) => prev + 1);
    const prev = () => {
        setCurrent((prev) => prev - 1);
        setLoading(false);
    };

    const connect = async (values: any) => {
        setLoading(true);
        const newValue = {
            ...initialValue,
            ...values,
            config: {
                ...initialValue.config,
                ...values.config
            }
        };
        try {
            const response = await client2.post('api/integration/connect', { ...newValue });
            if (initialValue.integration_type === 'api' && ![2, 3, 4].includes(initialValue.manufacture_type)) {
                const popupFeatures = 'width=600,height=700,scrollbars=yes,resizable=yes';
                const newWindow = window.open(response.data.data.linkUrl, '_blank', popupFeatures);
                if (newWindow) {
                    const handleMessage = (event: MessageEvent) => {
                        if (event.data?.success && event.data?.data) {
                            setIntegrationId(event.data.data.id);
                            mutate(event.data.data);
                            next();
                            window.removeEventListener("message", handleMessage);
                        }
                    };
                    window.addEventListener("message", handleMessage);
                    const checkClosed = setInterval(() => {
                        if (newWindow.closed) clearInterval(checkClosed);
                    }, 500);
                }
            } else if (initialValue.integration_type === 'mqtt') {
                const interval = setInterval(async () => {
                    try {
                        const response = await client2.get(`api/integration/check-mqtt/${values?.config?.client_id}`);
                        console.log("Checking MQTT:", response.data);
                        console.log(response.data.data.status)
                        if (response.data.data.status === true) {
                            toastr.success("MQTT Connected ✅");
                            clearInterval(interval);
                            const response = await client2.post(`api/integrations`, { ...newValue, manufacture_type: 1883 });
                            if (response.status == 201) {
                                setIntegrationId(response.data.data.id);
                                mutate(response.data.data);
                                next();
                            }
                        }
                    } catch (error) {
                        console.error("Error checking MQTT:", error);
                    }
                }, 5000);

            } else {
                setIntegrationId(response.data.data.id);
                mutate(response.data.data);
                next();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const steps = [
        {
            title: 'Select Type',
            content: (
                <SelectIntegrationType
                    onSubmit={(type: string) => {
                        setInitialValue((prev: any) => ({ ...prev, integration_type: type, config: { integration_type: type } }));
                        next();
                    }}
                    current={current}
                />
            ),
        },
        {
            title: 'Integrate',
            content: (
                <FirstContent
                    steps={4}
                    current={current}
                    onSubmit={(values: any) => {
                        setInitialValue((prev: any) => ({ ...prev, ...values }));
                        next();
                    }}
                    action={(value: string) => {
                        value === 'next' ? next() : prev()
                    }}
                    initialValues={initialValue}
                />
            ),
        },
        {
            title: 'Connect',
            content: (
                <SecondContent
                    onSubmit={connect}
                    initialValue={initialValue}
                    steps={4}
                    current={current}
                    prev={prev}
                    loading={loading}
                />
            ),
        },
        {
            title: 'Import',
            content: <ImportDevices integrationId={integrationId} loading={loading} setLoading={setLoading} />,
        },
    ];

    const items = steps.map((item) => ({ key: item.title, title: item.title }));

    return (
        <div>
            <Steps size="small" current={current} items={items} />
            <div className="mt-6">{steps[current].content}</div>
        </div>
    );
};

export default Integrate;

const SelectIntegrationType = ({
    onSubmit,
}: {
    onSubmit: (type: string) => void;
    current: number;
}) => {
    const cardStyle = `cursor-pointer border p-6 rounded-xl shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-300 w-60 h-52 text-center flex flex-col justify-center items-center`;

    const types = [
        { key: 'api', label: 'API Integration', icon: <Cloud size={32} className="mb-2 text-blue-500" /> },
        { key: 'mqtt', label: 'MQTT Integration', icon: <Server size={32} className="mb-2 text-green-500" /> },
    ];

    return (
        <div className="flex justify-center gap-10 mt-10">
            {types.map(({ key, label, icon }) => (
                <div
                    key={key}
                    onClick={() => onSubmit(key)}
                    className={cardStyle}
                >
                    {icon}
                    <h3 className="text-lg font-semibold capitalize">{label}</h3>
                </div>
            ))}
        </div>
    );
};

type FirstContentProps = {
    steps: number;
    current: number;
    onSubmit: any;
    initialValues: any;
    action: any;
};

const FirstContent: React.FC<FirstContentProps> = ({ steps, current, onSubmit, initialValues, action }) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const isAPI = initialValues.integration_type === 'api';
    const isMQTT = initialValues.integration_type === 'mqtt';

    const manufactureOptions = [
        { value: 1, label: "Huawei", devices: ["inverter", "meter", "battery"] },
        { value: 2, label: "GoodWe", devices: ["inverter"] },
        { value: 3, label: "DEDDIE", devices: ["meter"] },
        { value: 4, label: "ReMoni", devices: ["consumption"] },
        { value: 5, label: "Enphase", devices: ["inverter", "meter", "battery"] },
        { value: 6, label: "Fronius", devices: ["inverter"] },
        { value: 7, label: "Growatt", devices: ["inverter"] },
        { value: 8, label: "Hoymiles", devices: ["inverter"] },
        { value: 9, label: "SMA", devices: ["inverter", "meter", "battery"] },
        { value: 10, label: "Sofar", devices: ["inverter"] },
        { value: 11, label: "SolarEdge", devices: ["inverter", "meter", "battery"] },
        { value: 12, label: "Solax", devices: ["inverter"] },
        { value: 13, label: "Solis", devices: ["inverter"] },
        { value: 14, label: "Tesla", devices: ["inverter", "meter", "battery"] },
        { value: 15, label: "Sungrow", devices: ["inverter"] },
    ];

    const deviceTypeOptions = {
        inverter: { value: "inverter", label: "Inverter" },
        meter: { value: "meter", label: "Meter" },
        battery: { value: "battery", label: "Battery" },
        consumption: { value: "consumption", label: "Load" },
    } as any;

    return (
        <Form
            form={form}
            name="integrationForm"
            layout="vertical"
            autoComplete="off"
            initialValues={initialValues}
            onFinish={onSubmit}
        >
            <TextField name="name" label="Name" rules={[{ required: true }]} />

            {isAPI && (
                <Form.Item
                    label="Manufacturer"
                    name="manufacture_type"
                    rules={[{ required: true }]}
                >
                    <CustomSelectOptions
                        options={manufactureOptions}
                        onChange={(val: any) => {
                            form.setFieldValue("manufacture_type", val);
                            form.setFieldValue("device_type", undefined);
                        }}
                    />
                </Form.Item>
            )}

            <Form.Item dependencies={["manufacture_type"]} noStyle>
                {({ getFieldValue }) => {
                    const selected = manufactureOptions.find((m) => m.value === getFieldValue("manufacture_type"));
                    const deviceOptions = isAPI ? selected?.devices.map((d) => deviceTypeOptions[d]) || [] : Object.values(deviceTypeOptions);

                    return (
                        <Form.Item
                            label="Device Type"
                            name="device_type"
                            rules={[{ required: true }]}
                        >
                            <CustomSelectOptions options={deviceOptions} />
                        </Form.Item>
                    );
                }}
            </Form.Item>

            <Form.Item>
                <div className='flex space-x-5'>
                    <Button onClick={() => action('prev')}>Prev</Button>
                    <Button className='bg-blue-700 text-white' onClick={() => form.submit()}>Next</Button>
                </div>
            </Form.Item>
        </Form>
    );
};

type SecondContentProps = {
    current: any;
    steps: any;
    initialValue: any;
    prev: any;
    onSubmit: any;
    loading: boolean;
};

const SecondContent: React.FC<SecondContentProps> = ({ initialValue, current, steps, prev, onSubmit, loading }) => {
    const [form] = Form.useForm();
    const { role } = useSelector((state: any) => state.role);

    const isAPI = initialValue.integration_type === 'api';
    const isMQTT = initialValue.integration_type === 'mqtt';

    return (
        <Form form={form} layout="vertical" onFinish={onSubmit} autoComplete="off" onFinishFailed={() => { }}>
            {isAPI && initialValue.manufacture_type === 3 && (
                <>
                    <TextField name={['config', 'token']} type="password" label="Key" rules={[{ required: true }]} />
                    <TextField name={['config', 'taxNumber']} label="Tax Number" rules={[{ required: true }]} />
                </>
            )}

            {isAPI && [2, 4].includes(initialValue.manufacture_type) && (
                <>
                    <TextField name={['config', 'username']} label="User Name" rules={[{ required: true }]} />
                    <TextField name={['config', 'password']} type="password" label="Password" rules={[{ required: true }]} />
                </>
            )}

            {isMQTT && (
                <>
                    <Row className='flex space-x-3'>
                        <Col>
                            <TextField name={['config', 'client_id']} label="Client Id" disabled rules={[{ required: true }]} />

                        </Col>
                        <Col>
                            {form.getFieldValue(['config', 'client_id']) && (
                                <Tooltip title="Copy Topic">
                                    <Copy className='hover:cursor-pointer border-2 rounded-l mt-8 ' onClick={() => {
                                        const clientId = form.getFieldValue(['config', 'client_id']);
                                        navigator.clipboard.writeText(clientId);
                                    }} />
                                </Tooltip>
                            )}
                        </Col>
                        <Col>
                            <Tooltip title="Generate Client Id">
                                <RefreshCwIcon className='hover:cursor-pointer border-2 rounded-xl mt-8 '
                                    onClick={() => {
                                        let generateId = '';
                                        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                                        generateId = _.times(16, () => _.sample(chars)).join('');
                                        form.setFieldValue(['config', 'client_id'], generateId)
                                    }
                                    }
                                />

                            </Tooltip>
                        </Col>
                    </Row>
                    <TextField name={['config', 'user_name']} label="User Name" rules={[{ required: true }]} />
                    <TextField name={['config', 'password']} label="Password" rules={[{ required: true }]} />
                </>
            )}

            <div className="flex gap-3 mt-4">
                <Form.Item>
                    <LoadingButton loading={loading} title="Connect" />
                </Form.Item>
                <Form.Item>
                    <Button onClick={() => prev()}>Previous</Button>
                </Form.Item>
            </div>
        </Form>
    );
};
